/**
 * Normalize a name for duplicate comparison:
 * trim + lowercase + collapse internal whitespace.
 */
export const normalizeName = (value) =>
  value?.trim().toLowerCase().replace(/\s+/g, " ") ?? "";

/**
 * Generate a unique item code in the format abc1234
 * (3 random lowercase letters + 4 random digits)
 */
export const generateItemCode = () => {
  const letters = "abcdefghijklmnopqrstuvwxyz";
  const randomLetters = Array.from({ length: 3 }, () =>
    letters[Math.floor(Math.random() * letters.length)]
  ).join("");
  const randomDigits = String(Math.floor(1000 + Math.random() * 9000));
  return `${randomLetters}${randomDigits}`;
};

/**
 * Processing steps for the OCR loading screen
 */
export const PROCESSING_STEPS = [
  { id: 1, label: "extracting text" },
  { id: 2, label: "detecting categories" },
  { id: 3, label: "identifying items & prices" },
  { id: 4, label: "formatting digital layout" },
];

/**
 * Transform OCR API response into the UI-friendly format used by ConfirmMenu.
 *
 * API response shape:
 * {
 *   data: { restaurant: string, menu: { [categoryName]: [{ name, price }] } },
 *   summary: string,
 *   stats: { categories: number, items: number, pages?: number }
 * }
 *
 * UI format:
 * {
 *   menuName, menuImage, aiSummary,
 *   stats: { categories, items, pages },
 *   categories: [{ id, name, items: [{ id, name, description, price, image }] }]
 * }
 */
export const transformOcrResponse = (apiResponse) => {
  const { data, summary, stats } = apiResponse;

  let itemCounter = 0;
  let catCounter = 0;

  const categories = Object.entries(data.menu || {}).map(
    ([categoryName, items]) => {
      catCounter += 1;
      return {
        id: `cat-${catCounter}`,
        name: categoryName,
        items: (items || []).map((item) => {
          itemCounter += 1;
          // Parse price: "$5.99" → 5.99, "5.99" → 5.99, handle missing
          let priceNum = 0;
          if (item.price) {
            const cleaned = String(item.price).replace(/[^0-9.]/g, "");
            priceNum = parseFloat(cleaned) || 0;
          }
          return {
            id: `item-${itemCounter}`,
            name: item.name || "",
            description: item.description || "",
            price: priceNum,
            image: "",
          };
        }),
      };
    }
  );

  return {
    menuName: data.restaurant || "untitled menu",
    menuImage: "",
    aiSummary: summary || "",
    stats: {
      categories: stats?.categories || categories.length,
      items: stats?.items || itemCounter,
      pages: stats?.pages || 0,
      media: stats?.media || 0,
      fixes: stats?.fixes || 0,
    },
    categories,
  };
};

/**
 * Convert OCR UI selections into sequential API call payloads.
 *
 * @param {Object} menuData - { menuName, categories: [{ id, name, items: [{ id, name, description, price }] }] }
 * @param {Set<string>} selectedCategoryIds - checked category IDs
 * @param {Map<string, Set<string>>} selectedItemIds - Map<catId, Set<itemId>>
 * @returns {{ menuName: string, itemPayloads: Array, categoryPayloads: Array }}
 */
export const transformOcrToMenuPayload = (menuData, selectedCategoryIds, selectedItemIds) => {
  const itemPayloads = [];
  const categoryPayloads = [];

  (menuData.categories || []).forEach((cat) => {
    if (!selectedCategoryIds.has(cat.id)) return;
    const catItemSet = selectedItemIds.get(cat.id) || new Set();
    const selectedItems = cat.items.filter((item) => catItemSet.has(item.id));

    selectedItems.forEach((item) => {
      itemPayloads.push({
        _localId: item.id,
        name: item.name,
        description: item.description || "",
        price: item.price || 0,
      });
    });

    categoryPayloads.push({
      _localCatId: cat.id,
      name: cat.name,
      _localItemIds: selectedItems.map((i) => i.id),
    });
  });

  return { menuName: menuData.menuName, itemPayloads, categoryPayloads };
};
